import React, { useEffect, useState } from 'react';
import { Form, DatePicker, Select, Button, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import PropTypes from 'prop-types';
import { getAllApi } from 'api/crud';

const { RangePicker } = DatePicker;
const { Option } = Select;

const DashboardFilter = ({ onFilterChange, initialFilters = {} }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  
  const [companies, setCompanies] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [workingTypes, setWorkingTypes] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingIndustries, setLoadingIndustries] = useState(false);
  const [loadingWorkingTypes, setLoadingWorkingTypes] = useState(false);

  // Load companies
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoadingCompanies(true);
        const data = await getAllApi('companies', {
          filter: { isActive: { $eq: true } },
          limit: 1000,
        });
        if (data?.data) {
          setCompanies(data.data);
        }
      } catch (error) {
        console.error('Error loading companies:', error);
      } finally {
        setLoadingCompanies(false);
      }
    };
    loadCompanies();
  }, []);

  // Load industries
  useEffect(() => {
    const loadIndustries = async () => {
      try {
        setLoadingIndustries(true);
        const data = await getAllApi('industries', { limit: 1000 });
        if (data?.data) {
          setIndustries(data.data);
        }
      } catch (error) {
        console.error('Error loading industries:', error);
      } finally {
        setLoadingIndustries(false);
      }
    };
    loadIndustries();
  }, []);

  // Load working types
  useEffect(() => {
    const loadWorkingTypes = async () => {
      try {
        setLoadingWorkingTypes(true);
        const data = await getAllApi('working-types', { limit: 1000 });
        if (data?.data) {
          setWorkingTypes(data.data);
        }
      } catch (error) {
        console.error('Error loading working types:', error);
      } finally {
        setLoadingWorkingTypes(false);
      }
    };
    loadWorkingTypes();
  }, []);

  const handleSubmit = (values) => {
    const filters = {};
    
    if (values.dateRange && values.dateRange.length === 2) {
      filters.dateFrom = values.dateRange[0].startOf('day').toISOString();
      filters.dateTo = values.dateRange[1].endOf('day').toISOString();
    }
    
    if (values.companyId) {
      filters.companyId = values.companyId;
    }
    
    if (values.industryId) {
      filters.industryId = values.industryId;
    }
    
    if (values.workingTypeId) {
      filters.workingTypeId = values.workingTypeId;
    }
    
    onFilterChange(filters);
  };

  const handleReset = () => {
    form.resetFields();
    onFilterChange({});
  };

  const initialValues = {};
  if (initialFilters.dateFrom && initialFilters.dateTo) {
    initialValues.dateRange = [
      moment(initialFilters.dateFrom),
      moment(initialFilters.dateTo),
    ];
  }
  if (initialFilters.companyId) {
    initialValues.companyId = initialFilters.companyId;
  }
  if (initialFilters.industryId) {
    initialValues.industryId = initialFilters.industryId;
  }
  if (initialFilters.workingTypeId) {
    initialValues.workingTypeId = initialFilters.workingTypeId;
  }

  return (
    <Form
      form={form}
      layout="inline"
      onFinish={handleSubmit}
      initialValues={initialValues}
      style={{ marginBottom: 24 }}
    >
      <Row gutter={[16, 16]} style={{ width: '100%' }}>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="dateRange" label={t('dashboard.filter.dateRange')}>
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="companyId" label={t('dashboard.filter.company')}>
            <Select
              style={{ width: '100%' }}
              placeholder={t('dashboard.filter.company')}
              showSearch
              allowClear
              loading={loadingCompanies}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {companies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="industryId" label={t('dashboard.filter.industry')}>
            <Select
              style={{ width: '100%' }}
              placeholder={t('dashboard.filter.industry')}
              showSearch
              allowClear
              loading={loadingIndustries}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {industries.map((industry) => (
                <Option key={industry.id} value={industry.id}>
                  {industry.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="workingTypeId" label={t('dashboard.filter.workingType')}>
            <Select
              style={{ width: '100%' }}
              placeholder={t('dashboard.filter.workingType')}
              showSearch
              allowClear
              loading={loadingWorkingTypes}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {workingTypes.map((workingType) => (
                <Option key={workingType.id} value={workingType.id}>
                  {workingType.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24}>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              {t('button.search')}
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={handleReset}>
              {t('button.reset')}
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

DashboardFilter.propTypes = {
  onFilterChange: PropTypes.func.isRequired,
  initialFilters: PropTypes.object,
};

export default DashboardFilter;
